"use client";

import * as React from "react";

/** How far the section's top must rise into the viewport before the controls come up. */
const CONTROLS_REVEAL_FRACTION = 0.4;

/**
 * Whether the sticky controls bar should be up: once the section's top has risen 40% of the
 * viewport (the heading and film button are in view; the LP's bottom strip tucks away at the same point, so the two
 * hand over), and after it has scrolled past. Scrolling back above the section lowers it again.
 */
export function useControlsReveal(ref: React.RefObject<Element | null>) {
  const [revealed, setRevealed] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === "undefined") {
      // No observer support: keep the controls up.
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        const rootTop = entry.rootBounds?.top ?? 0;
        setRevealed(
          entry.isIntersecting || entry.boundingClientRect.bottom <= rootTop,
        );
      },
      { rootMargin: `0px 0px -${CONTROLS_REVEAL_FRACTION * 100}% 0px` },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return revealed;
}
